import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

// ─── ThemeContext ───────────────────────────────────────────────────────────────
// Тема (dark/light) зберігається у двох місцях:
// - localStorage — миттєве застосування на будь-якому наступному завантаженні
//   цього браузера, без очікування відповіді сервера (без "блимання" темою).
// - users.theme (БД, через PATCH /users/:id) — щоб вибір переносився між
//   пристроями/браузерами при логіні під тим самим акаунтом.
//
// ВАЖЛИВО: localStorage-ключ прив'язаний до userId ('cp-theme:<id>'), а НЕ
// спільний на весь браузер. Інакше, якщо на одному комп'ютері по черзі
// заходять різні акаунти (адмін і фрілансер), вибір теми одного протікав би
// в кабінет іншого після оновлення сторінки — це і був баг, який тут
// виправляється. 'cp-active-user' — вказівник на останнього залогіненого
// юзера в цьому браузері, потрібен лише для того, щоб anti-FOUC скрипт у
// index.html міг здогадатись, чию тему застосувати ДО того, як React встигне
// дізнатись, хто саме залогінений (сесія відновлюється асинхронно).

const ACTIVE_USER_KEY = 'cp-active-user';
const ThemeContext = createContext(null);

function themeKey(userId) {
  return `cp-theme:${userId}`;
}
function readActiveUserId() {
  try { return localStorage.getItem(ACTIVE_USER_KEY); } catch { return null; }
}
function readStored(userId) {
  if (!userId) return null;
  try { return localStorage.getItem(themeKey(userId)); } catch { return null; }
}
function writeStored(userId, theme) {
  if (!userId) return;
  try {
    localStorage.setItem(themeKey(userId), theme);
    localStorage.setItem(ACTIVE_USER_KEY, String(userId));
  } catch { /* ignore */ }
}
function systemPref() {
  return (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches)
    ? 'light' : 'dark';
}

export function ThemeProvider({ children, currentUser = null }) {
  const userId = currentUser?.id ?? null;

  const [theme, setThemeState] = useState(() => {
    if (userId) {
      const stored = readStored(userId);
      if (stored === 'dark' || stored === 'light') return stored;
      if (currentUser?.theme === 'dark' || currentUser?.theme === 'light') return currentUser.theme;
      return systemPref();
    }
    // До логіну (Login/bootstrap-екран) підглядаємо тему останнього юзера
    // цього браузера, щоб не блимнути "неправильною" темою при завантаженні.
    const lastUser = readActiveUserId();
    const stored = lastUser ? readStored(lastUser) : null;
    return (stored === 'dark' || stored === 'light') ? stored : systemPref();
  });

  // При зміні залогіненого юзера (логін/логаут/інший акаунт у цьому ж
  // браузері) — перезавантажити тему саме під нього, а не лишати попередню.
  useEffect(() => {
    if (!userId) return;
    const stored = readStored(userId);
    if (stored === 'dark' || stored === 'light') {
      setThemeState(stored);
    } else if (currentUser?.theme === 'dark' || currentUser?.theme === 'light') {
      setThemeState(currentUser.theme);
    } else {
      setThemeState(systemPref());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (userId) writeStored(userId, theme);
  }, [theme, userId]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    if (userId) {
      writeStored(userId, next);
      api.users.update(userId, { theme: next }).catch(() => { /* fire-and-forget */ });
    }
  }, [userId]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
